import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { Notification, NotificationChannelMessage } from "@models/notification";
import { Consumer, createConsumer, Subscription } from "@rails/actioncable";
import { Subject } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./local-storage.service";
import { NotificationStore } from "./notification.store";

const CHANNEL = "NotificationsChannel";

/**
 * Conexão em tempo real com o NotificationsChannel (ActionCable).
 * O canal usa `stream_for current_user`, então o usuário é resolvido pelo
 * token JWT enviado na URL do cable (?token=...). Sem params na subscrição.
 */
@Injectable({
  providedIn: "root",
})
export class NotificationRealtimeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly store = inject(NotificationStore);

  private consumer?: Consumer;
  private subscription?: Subscription;

  private readonly newNotificationSubject = new Subject<Notification>();
  /** Emite somente notificações novas recebidas via WS (não a carga REST). */
  readonly newNotification$ = this.newNotificationSubject.asObservable();

  /** Idempotente: só assina uma vez. */
  connect(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.subscription) {
      return;
    }

    const token = this.localStorageService.getItem("auth-token");
    if (!token) {
      return;
    }

    const cableUrl = this.buildCableUrl(token);
    this.consumer = createConsumer(cableUrl);
    this.subscription = this.consumer.subscriptions.create(
      { channel: CHANNEL },
      {
        received: (message: NotificationChannelMessage) =>
          this.handleMessage(message),
        rejected: () => console.error(`${CHANNEL} subscription rejected`),
      },
    );
  }

  disconnect(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.consumer?.disconnect();
    this.consumer = undefined;
  }

  private handleMessage(message: NotificationChannelMessage): void {
    if (!message?.type) {
      return;
    }

    switch (message.type) {
      case "notification.new":
        this.store.prepend(message.notification);
        this.newNotificationSubject.next(message.notification);
        break;
      case "notifications.all_read":
        this.store.zeroUnread();
        break;
    }
  }

  private buildCableUrl(token: string): string {
    const wsBase = environment.API_BACKEND.replace(/^http/, "ws");
    return `${wsBase}/cable?token=${encodeURIComponent(token)}`;
  }
}
